import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatChipsModule } from '@angular/material/chips';
import { SimulationResponse, SimulationResults } from '../../core/models/simulation.model';

@Component({
  selector: 'app-simulation-directions-table',
  standalone: true,
  imports: [
    CommonModule,
    MatChipsModule,
  ],
  templateUrl: './simulation-directions-table.component.html',
  styleUrl: './simulation-directions-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SimulationDirectionsTableComponent<T extends SimulationResults> {
  @Input() simulationResponse!: SimulationResponse<T> | null;
  @Input() directions: string[] = ['x', 'y', 'z'];

  getResultByDirection(direction: string): T | undefined {
    return this.simulationResponse?.results?.find((result: T) =>
      result.direction?.toLowerCase() === direction.toLowerCase()
    );
  }

  formatDateTime(dateTime: string | null): string {
    if (!dateTime) return '—';

    try {
      const date = new Date(dateTime);
      const formatted = date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
      return formatted.replace(',', '');
    } catch {
      return '—';
    }
  }

  calculateDuration(startTime: string | null, endTime: string | null): string {
    if (!startTime || !endTime) return '—';

    try {
      const start = new Date(startTime).getTime();
      const end = new Date(endTime).getTime();
      const durationMs = end - start;

      if (durationMs < 0) return '—';

      const seconds = Math.floor(durationMs / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (days > 0) {
        return `${days}d ${hours % 24}h ${minutes % 60}m`;
      } else if (hours > 0) {
        return `${hours}h ${minutes % 60}m`;
      } else if (minutes > 0) {
        return `${minutes}m`;
      } else {
        return '< 1m';
      }
    } catch {
      return '—';
    }
  }

  getStatusClass(status: string): string {
    if (!status) return 'status-not-started';

    switch (status.toLowerCase()) {
      case 'failure':
        return 'status-failed';
      case 'initialization':
        return 'status-initialization';
      case 'running':
        return 'status-running';
      case 'success':
      case 'completed':
        return 'status-success';
      default:
        return 'status-default';
    }
  }
}
